import { useEffect, useRef } from "react";

const STORAGE_PREFIX = "catalogue-scroll:";

type SavedState = { pages: number; scrollY: number };

function readSaved(key: string): SavedState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    return raw ? (JSON.parse(raw) as SavedState) : null;
  } catch {
    return null;
  }
}

function writeSaved(key: string, s: SavedState) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(s));
  } catch {
    /* quota */
  }
}

function clearSaved(key: string) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* noop */
  }
}

/**
 * Persists catalogue browse state (page depth + scroll Y) per filter key so
 * back-nav from an OEM detail page restores the same view. Filter/sort state
 * itself lives in URL search params — this hook only handles infinite-scroll
 * depth and pixel offset, which are not URL-serializable.
 *
 * Usage:
 *   const restore = useCatalogueScrollRestore(key, {
 *     currentPages, hasNextPage, fetchNextPage,
 *   });
 *   // Call restore.markLink() from card onClick to persist state.
 */
export function useCatalogueScrollRestore(
  key: string,
  opts: {
    currentPages: number;
    hasNextPage: boolean;
    fetchNextPage: () => Promise<unknown> | unknown;
  },
) {
  const { currentPages, hasNextPage, fetchNextPage } = opts;
  const restoredRef = useRef(false);
  const targetPagesRef = useRef<number | null>(null);
  const targetYRef = useRef<number | null>(null);

  // On mount, read saved state.
  useEffect(() => {
    const saved = readSaved(key);
    if (saved && saved.pages > 1) {
      targetPagesRef.current = saved.pages;
      targetYRef.current = saved.scrollY;
    } else if (saved) {
      targetYRef.current = saved.scrollY;
      restoredRef.current = false;
    }
    // We do NOT clear here — persist across accidental reloads too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Drive fetchNextPage until we hit the target page count, then scroll.
  useEffect(() => {
    if (restoredRef.current) return;
    const targetPages = targetPagesRef.current;
    const targetY = targetYRef.current;
    if (targetPages && currentPages < targetPages && hasNextPage) {
      void fetchNextPage();
      return;
    }
    if (targetY != null) {
      // Wait a tick for the DOM to lay out.
      const raf = requestAnimationFrame(() => {
        window.scrollTo({ top: targetY, behavior: "auto" });
        restoredRef.current = true;
      });
      return () => cancelAnimationFrame(raf);
    }
    restoredRef.current = true;
  }, [key, currentPages, hasNextPage, fetchNextPage]);

  // Save on outbound link click.
  const markLink = () => {
    writeSaved(key, { pages: currentPages, scrollY: window.scrollY });
  };

  // Reset saved state when filters change to a different key.
  const reset = () => clearSaved(key);

  return { markLink, reset };
}
